// ---------------------------------------------------------------
// TagAlong Shop — the product catalog the admin sells and the orders customers
// place from inside the app. Both live on the backend (/shop): every customer sees
// the same catalog, an admin sees every order, a customer only their own, and an
// order is marked paid only after the server checks the payment with Stripe.
//
// A localStorage mirror gives instant reads (and an offline fallback).
// ---------------------------------------------------------------
import { API_BASE } from './config';

const CACHE = 'ta-shop-cache';

// ---- payments (Stripe Checkout, handled by the backend) ----
const jwtHeader = () => { try { const t = localStorage.getItem('ta-jwt'); return t ? { Authorization: `Bearer ${t}` } : {}; } catch { return {}; } };

// Is card payment configured on the backend? (false → app falls back to place-order)
export async function stripeEnabled() {
  if (!API_BASE) return false;
  try { const r = await fetch(`${API_BASE}/stripe/config`); const d = await r.json(); return !!d.enabled; } catch { return false; }
}
// Start a hosted Stripe Checkout for these items; returns the URL to redirect to.
export async function startCheckout(items, orderId) {
  const r = await fetch(`${API_BASE}/stripe/checkout`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...jwtHeader() },
    body: JSON.stringify({ items, orderId }),
  });
  if (!r.ok) { const d = await r.json().catch(() => ({})); const e = new Error(d.error || 'Checkout failed.'); e.code = d.error; throw e; }
  return (await r.json()).url;
}
// Confirm a completed checkout on return from Stripe.
export async function verifyPayment(sessionId) {
  try {
    const r = await fetch(`${API_BASE}/stripe/verify?session_id=${encodeURIComponent(sessionId)}`, { credentials: 'include', headers: jwtHeader() });
    if (!r.ok) return { paid: false };
    return await r.json();
  } catch { return { paid: false }; }
}

// Seed catalog used until the admin saves their own. Editable in the admin
// Products manager. `perks` is a short bullet list shown on the product card.
export const DEFAULT_PRODUCTS = [
  {
    id: 'dev-obd', name: 'TagAlong OBD Tracker', price: 149, kind: 'device', deviceCount: 1, active: true,
    description: 'Plug-and-play tracker that reads your car’s computer through the OBD-II port. Self-install in seconds.',
    perks: ['Live GPS location & trip history', 'Check-engine codes & vehicle health', 'No wiring — plug in and go'],
  },
  {
    id: 'dev-hw', name: 'TagAlong Hardwired Tracker', price: 179, kind: 'device', deviceCount: 1, active: true,
    description: 'Hidden, hardwired tracker for theft protection and always-on tracking. Professional install recommended.',
    perks: ['Concealed & tamper-resistant', 'Ignition + battery monitoring', 'Best for theft recovery'],
  },
];

const readCache = () => { try { return JSON.parse(localStorage.getItem(CACHE)); } catch { return null; } };
const writeCache = (shop) => { try { localStorage.setItem(CACHE, JSON.stringify(shop)); } catch { /* ignore */ } };
const normalize = (shop) => ({ products: (shop && shop.products) || DEFAULT_PRODUCTS, orders: (shop && shop.orders) || [] });

const shopCall = async (path, method = 'GET', body) => {
  const r = await fetch(`${API_BASE}${path}`, { method, credentials: 'include', headers: { 'Content-Type': 'application/json', ...jwtHeader() }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Shop request failed (${r.status}).`);
  return j;
};

// Read the live shop (and refresh the cache).
export async function fetchShop() {
  try {
    const shop = normalize(await shopCall('/shop'));
    writeCache(shop);
    return shop;
  } catch {
    return readCache() || normalize(null);
  }
}

// Synchronous read for first paint — the cache, or the seed catalog.
export const getCachedShop = () => readCache() || normalize(null);
export const getProductsCached = () => getCachedShop().products;
export const productById = (id, products) => (products || getProductsCached()).find((p) => p.id === id) || null;

// admin: save the whole catalog
async function writeProducts(mutator) {
  const current = normalize(await shopCall('/shop'));
  const products = mutator(current.products);
  const next = normalize(await shopCall('/shop/products', 'PUT', { products }));
  writeCache({ ...current, products: next.products });
  return { ...current, products: next.products };
}

// ---- products (admin) ----
export async function saveProduct(p) {
  return writeProducts((list) => {
    const products = [...list];
    const rec = { active: true, kind: 'device', deviceCount: 1, perks: [], ...p, id: p.id || `prod${Date.now()}`, price: Number(p.price) || 0 };
    const i = products.findIndex((x) => x.id === rec.id);
    if (i >= 0) products[i] = rec; else products.push(rec);
    return products;
  });
}
export async function deleteProduct(id) {
  return writeProducts((list) => list.filter((x) => x.id !== id));
}

// ---- orders ----
export const orderTotal = (order, products) => (order.items || []).reduce((sum, it) => {
  const p = productById(it.productId, products); return sum + (p ? p.price * (it.qty || 1) : 0);
}, 0);
export const orderDeviceCount = (order, products) => (order.items || []).reduce((n, it) => {
  const p = productById(it.productId, products); return n + (p ? (p.deviceCount || 0) * (it.qty || 1) : 0);
}, 0);

export const ORDER_STATUSES = [
  { id: 'new', label: 'New', color: '#f97316' },
  { id: 'provisioning', label: 'Provisioning', color: '#0284c7' },
  { id: 'shipped', label: 'Shipped', color: '#7c3aed' },
  { id: 'active', label: 'Active', color: '#16a34a' },
];
export const statusById = (id) => ORDER_STATUSES.find((s) => s.id === id) || ORDER_STATUSES[0];

// A customer places an order from the Shop. Recorded server-side for the admin.
export async function placeOrder(order) {
  return shopCall('/shop/orders', 'POST', order);
}
export async function fetchOrders() { return (await fetchShop()).orders; }
// admin: change an order (status, notes…)
export async function updateOrder(id, patch) { return shopCall(`/shop/orders/${encodeURIComponent(id)}`, 'PATCH', patch); }
export async function removeOrder(id) { return shopCall(`/shop/orders/${encodeURIComponent(id)}`, 'DELETE'); }
// customer back from Stripe: the server checks the payment and marks the order paid
export async function markPaid(id, sessionId) { return shopCall(`/shop/orders/${encodeURIComponent(id)}/paid`, 'POST', { sessionId }); }
